<template>
  <div class="acc-browser">
    <!-- Estado: empresa no conectada -->
    <div v-if="companyNotConnected" class="acc-empty-state">
      <b-icon pack="fas" icon="unlink" size="is-large" />
      <p class="mt-3 mb-3">{{ $t('autodesk.viewer.company_not_connected') }}</p>
      <p class="acc-hint">{{ $t('autodesk.viewer.company_not_connected_hint') }}</p>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="acc-loading">
      <b-icon pack="fas" icon="spinner" custom-class="fa-spin" />
      <span class="ml-2">{{ $t('autodesk.browser.loading') }}</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="acc-error">
      <b-icon pack="fas" icon="exclamation-triangle" />
      <span class="ml-2">{{ error }}</span>
    </div>

    <!-- Sin recursos autorizados para este proyecto (o solo carpetas sin items) -->
    <div v-else-if="viewableResources.length === 0" class="acc-empty-state">
      <b-icon pack="fas" icon="folder-open" size="is-large" />
      <p class="mt-2">{{ $t('autodesk.viewer.no_resources_for_project') }}</p>
      <p class="acc-hint">{{ $t('autodesk.viewer.no_resources_hint') }}</p>
    </div>

    <!-- Lista de recursos autorizados agrupados por carpeta origen (colapsables) -->
    <div v-else>
      <div v-for="group in groupedResources" :key="group.path" class="acc-group">
        <div v-if="group.path" class="acc-group-header is-clickable"
             @click="toggleGroup(group.path)"
             :title="group.path">
          <b-icon pack="fas" :icon="isOpen(group.path) ? 'folder-open' : 'folder'" size="is-small" />
          <span class="acc-group-name">{{ shortFolderName(group.path) }}</span>
          <span class="acc-group-count">{{ group.items.length }}</span>
          <b-icon pack="fas" :icon="isOpen(group.path) ? 'chevron-up' : 'chevron-down'" size="is-small" />
        </div>
        <ul v-if="isOpen(group.path)" class="acc-list">
          <li v-for="r in group.items" :key="r.id"
              class="acc-list-item is-clickable"
              :class="{ 'is-selected': selectedResourceId === r.id }"
              @click="selectResource(r)">
            <div class="acc-item-icon">
              <b-icon pack="fas" icon="file" />
            </div>
            <div class="acc-item-body">
              <div class="acc-item-name">{{ r.display_name || r.resource_urn }}</div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import store from '~/store'

export default {
  name: 'acc-project-browser',

  props: {
    projectId: { type: [Number, String], required: true },
  },

  data: () => ({
    loading: true,
    error: null,
    companyNotConnected: false,
    resources: [],
    selectedResourceId: null,
    // Set de paths cerrados — por defecto todos abiertos.
    closedGroups: new Set(),
  }),

  async mounted() {
    await this.loadResources()
  },

  computed: {
    // El usuario final solo ve recursos "abribles" (modelos). Carpetas, hubs y
    // ACC projects se filtran — están en el ACL para contexto admin, no para
    // visualizar. Lo que el usuario necesita son los .nwd / .rvt / .dwg abribles.
    viewableResources() {
      return this.resources.filter(r => this.isViewable(r.resource_urn))
    },

    // Agrupamos por parent_path para mostrar modelos juntos por carpeta origen.
    groupedResources() {
      const groups = {}
      this.viewableResources.forEach(r => {
        const key = r.parent_path || ''
        if (!groups[key]) groups[key] = { path: key, items: [] }
        groups[key].items.push(r)
      })
      return Object.values(groups)
    },
  },

  methods: {
    async loadResources() {
      this.loading = true
      this.error = null
      try {
        // Comprobar conexión corporativa primero.
        const statusResp = await axios.get('/api/v2/autodesk/aps/status', this.authHeader())
        // needs_reauth: hay token pero el refresh_token murió; a efectos del usuario es "no conectada".
        if (!statusResp.data?.data?.connected || statusResp.data?.data?.needs_reauth) {
          this.companyNotConnected = true
          return
        }

        const { data } = await axios.get(
          `/api/v2/autodesk/aps/projects/${this.projectId}/resources`,
          this.authHeader()
        )
        if (data.success) this.resources = data.data
        else this.error = data.message
      } catch (e) {
        this.error = e.response?.data?.message || e.message
      } finally {
        this.loading = false
      }
    },

    selectResource(r) {
      if (!this.isViewable(r.resource_urn)) return
      this.selectedResourceId = r.id
      this.$emit('item-selected', {
        urn: r.resource_urn,
        // La región viene de la fila del ACL (persistida al añadir en el picker).
        // Si por legacy la fila no la tiene, EMEA default.
        region: r.autodesk_region || 'EMEA',
        displayName: r.display_name,
      })
    },

    // Solo son abribles los URNs derivative base64 (resultado de la traducción
    // Autodesk, que es lo que el Viewer sabe cargar). Filtramos:
    //   - Hubs y ACC projects: patrón 'b.<uuid>' o 'a.<uuid>' sin prefijo urn:
    //   - Folders: contienen 'fs.folder' en el URN crudo
    //   - Raw lineage URNs: 'dm.lineage' (no son derivatives aún resueltos)
    isViewable(urn) {
      if (typeof urn !== 'string') return false
      if (/^[ab]\./.test(urn)) return false
      if (urn.includes('fs.folder')) return false
      if (urn.includes('dm.lineage')) return false
      return true
    },

    iconForUrn(urn) {
      if (typeof urn !== 'string') return 'file'
      if (/^[ab]\./.test(urn)) return 'folder-tree'
      if (urn.includes('fs.folder')) return 'folder'
      return 'file'
    },

    // ─── Agrupación colapsable ───
    isOpen(path) {
      return !this.closedGroups.has(path)
    },
    toggleGroup(path) {
      const next = new Set(this.closedGroups)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      this.closedGroups = next
    },
    // Nombre corto = último segmento del path. Si está vacío, muestra "/".
    shortFolderName(path) {
      if (!path) return '/'
      const parts = path.split(' / ')
      return parts[parts.length - 1] || path
    },

    authHeader() {
      return { headers: { Authorization: 'Bearer ' + store.getters['auth/token'] } }
    },
  },
}
</script>

<style scoped>
.acc-browser {
  padding: 1rem;
  background: white;
  border-radius: 6px;
  min-height: 400px;
}

.acc-loading, .acc-error {
  padding: 1rem;
  display: flex;
  align-items: center;
}

.acc-error {
  background: #fff5f5;
  color: #c53030;
  border-radius: 4px;
}

.acc-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #888;
}

.acc-hint {
  font-size: 0.85rem;
  color: #999;
  font-style: italic;
  margin-top: 8px;
}

.acc-group {
  margin-bottom: 12px;
}

.acc-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #eef1f6;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #2f374a;
  font-weight: 500;
  margin-bottom: 4px;
  user-select: none;
}
.acc-group-header.is-clickable { cursor: pointer; }
.acc-group-header.is-clickable:hover { background: #dde4ee; }
.acc-group-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.acc-group-count {
  background: #cfd8e3; color: #2f374a;
  padding: 1px 8px; border-radius: 10px;
  font-size: 0.75rem; font-weight: 600;
  min-width: 20px; text-align: center;
}

.acc-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.acc-list-item {
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem;
  gap: 0.6rem;
  border-radius: 4px;
}

.acc-list-item.is-clickable {
  cursor: pointer;
}

.acc-list-item.is-clickable:hover {
  background: #f5f7fb;
}

.acc-list-item.is-selected {
  background: #e8f0fe;
}

.acc-item-icon {
  color: #666;
  width: 2rem;
  text-align: center;
}

.acc-item-body {
  flex: 1;
  min-width: 0;
}

.acc-item-name {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
